import {createSelector} from 'reselect';

const INSURANCES_TO_ADD = ['FARE_RULE', 'INSURANCE', 'SERVICE_OPTION'];
const POSTSELL_TO_FILTER = ['FLEXIBLE_FARE_RULE', 'PREMIUM_FARE_RULE'];

const dpPriceFees = (store) => (store.dpPriceFees || {});
const getShoppingCart = (store) => (store.shoppingCart || {}).shoppingCart || {};
const getPrime = (store) => (store.prime || {}).primeMemberSelected || {};
const getExpectedPrice = (store) => store.pricing.expectedPrice;
const getRepricingLog = (store) => store.pricing.createShoppingCartRepricingValue;
const getResidentDiscountApplied = (store) => store.pricing.residentDiscountApplied || false;
const getSelectedPaymentMethod = (store) => store.pricing.selectedPaymentMethod || null;
const getRepricingAmount = (store) => store.pricing.repricingAmount || 0;
const getProductSelection = (store) => store.productSelection || {};
const getProducts = (productSelection) => productSelection.products || [];
const genericProductSum = (products) => products.reduce((sum, product) => sum + product.price.total, 0);
const baggagesPrice = (baggages) => genericProductSum(baggages);
const baggagesPrimePerks = (baggages, prop) => baggages.reduce((sum, product) => sum - (product.usePrimePrice
    ? product.perks[prop] : 0), 0);
const seatsPrice = (seats) => genericProductSum(seats);
const seatsPrimePerks = (seats, prop) => seats.reduce((sum, product) => sum - (product.usePrimePrice
    ? product.perks[prop] : 0), 0);
const insuranceAndServicesPrice = (products) => genericProductSum(products);
const getCheapestCard = (cards) => {
    return cards.reduce((cheapestCardFee, card) => {
        return Math.min(card.fee, cheapestCardFee);
    }, Infinity);
};

const getCheapestFee = (paymentList) => {
    return paymentList.length > 0 && paymentList.reduce((cheapestFee, paymentMethod) => {
        let fee = cheapestFee;
        if (paymentMethod.method === 'CREDITCARD') {
            fee = getCheapestCard(paymentMethod.info.cardTypes);
        } else {
            fee = paymentMethod.fee;
        }
        return Math.min(fee, cheapestFee);
    }, Infinity) || 0;
};

const getInsuranceTotalPrice = (insuranceAddedList = []) => {
    let insurancesTotalPrice = 0;
    insuranceAddedList.forEach((items) => {
        const insuranceAddedTotalPrice = items.totalPrice;
        insurancesTotalPrice += insuranceAddedTotalPrice;
    });
    return insurancesTotalPrice;
};

const getPrimeSelector = createSelector(getPrime, (primeMemberSelected) => primeMemberSelected.isSubscriptionAdded);
const expectedPriceSelector = createSelector(getExpectedPrice, (expectedPrice) => expectedPrice);
const repricingLogSelector = createSelector(getRepricingLog, (createShoppingCartPrice) => createShoppingCartPrice);
const residentDiscountAppliedSelector = createSelector(getResidentDiscountApplied, (residentDiscountApplied) => residentDiscountApplied);
const selectedPaymentMethodSelector = createSelector(getSelectedPaymentMethod, (selectedPaymentMethod) => selectedPaymentMethod);
const productsSelector = createSelector(getProductSelection, getProducts);
const accommodationShoppingItemSelector = createSelector(getShoppingCart, (shoppingCart) => shoppingCart.accommodationShoppingItem);
const membershipFeeSelector = createSelector(getShoppingCart, (shoppingCart) => shoppingCart.membershipPerksFee);
const numTravellersSelector = createSelector(getShoppingCart, (shoppingCart) => {
    const travellerInformation = shoppingCart.requiredTravellerInformation;
    return travellerInformation && travellerInformation.length || 1;
});
const itineraryPriceSelector = createSelector(
    getShoppingCart,
    getSelectedPaymentMethod,
    getRepricingAmount,
    getPrimeSelector,
    dpPriceFees,
    (shoppingCart, selectedPaymentMethod, repricingAmount, isSubscriptionAdded, dpPriceFees) => {
        const {itineraryShoppingItem = {}, accommodationShoppingItem, otherProductsShoppingItems = {},
            shoppingCartPriceCalculatorDTO = {}, totalPrice, paymentResultsDTO} = shoppingCart;
        const paymentList = paymentResultsDTO && paymentResultsDTO.payments || [];
        const selectedPaymentMethodFee = selectedPaymentMethod && selectedPaymentMethod.fee;
        const cheapestFee = typeof selectedPaymentMethodFee === 'number'
            ? selectedPaymentMethodFee
            : getCheapestFee(paymentList);
        const subscriptionCheapestFee = cheapestFee > 0 && isSubscriptionAdded ? 0 : Math.abs(cheapestFee);
        const {
            insuranceShoppingItems = [],
            postsellServiceOptionsItems = [],
            additionalProductShoppingItems = [],
        } = otherProductsShoppingItems || {};
        let nonSelectableInsurances = insuranceShoppingItems;
        let postSellTotalPrice = 0;
        let additionalServicesTotalPrice = 0;
        // eslint-disable-next-line no-restricted-properties
        if (!Odigeo.AB.isKeepAncillariesSelectionOF2) {
            nonSelectableInsurances = insuranceShoppingItems.filter(({selectable}) => !selectable);
        } else {
            const postsellWithoutFareRule = postsellServiceOptionsItems.filter(({name}) => !POSTSELL_TO_FILTER.includes(name));
            postSellTotalPrice = getInsuranceTotalPrice(postsellWithoutFareRule);
            additionalServicesTotalPrice = getInsuranceTotalPrice(additionalProductShoppingItems);
        }
        const fees = shoppingCartPriceCalculatorDTO ? shoppingCartPriceCalculatorDTO.feeInfoDTO || {} : {};
        const priceCalculator = Object.keys(fees).reduce(
            (feesMap, feeKey) => {
                feesMap.taxSum = feesMap.taxSum + fees[feeKey].tax;
                feesMap.discountSum = feesMap.discountSum + fees[feeKey].discount;
                return feesMap;
            },
            {taxSum: 0, discountSum: 0}
        );
        let itemPriceToAdd = priceCalculator.taxSum;
        itemPriceToAdd = accommodationShoppingItem ? accommodationShoppingItem.price + itemPriceToAdd : itemPriceToAdd;
        const itineraryPriceCalculation = itineraryShoppingItem.price - subscriptionCheapestFee +
            shoppingCartPriceCalculatorDTO.markup + itemPriceToAdd - priceCalculator.discountSum;
        const nonSelectableInsurancesTotalPrice = getInsuranceTotalPrice(nonSelectableInsurances);
        const itineraryPrice = repricingAmount || itineraryPriceCalculation;
        const itineraryPricePaxDPNoFees = calculateItineraryPricePaxDPNoFees(
            dpPriceFees,
            accommodationShoppingItem,
            itineraryShoppingItem,
            shoppingCartPriceCalculatorDTO,
            priceCalculator,
            shoppingCart,
            cheapestFee
        );
        return {
            servicesTotalPrice: postSellTotalPrice + additionalServicesTotalPrice,
            itineraryPricePaxDPNoFees,
            itineraryPrice,
            nonSelectableInsurancesTotalPrice,
            totalPrice,
            cheapestFee,
        };
    }
);

const calculateItineraryPricePaxDPNoFees = (
    dpPriceFees,
    accommodationShoppingItem,
    itineraryShoppingItem,
    shoppingCartPriceCalculatorDTO,
    priceCalculator,
    shoppingCart,
    cheapestFee) => {
    const isMeta = dpPriceFees && dpPriceFees.isMeta;
    const pageName = dpPriceFees && dpPriceFees.page;
    const isMetasPricePresentation = isMeta && pageName !== 'payment';
    const hasPriceConfiguration = accommodationShoppingItem && accommodationShoppingItem.priceConfiguration;
    const isFullPrice = hasPriceConfiguration && accommodationShoppingItem.priceConfiguration.fullPrice;
    if (isFullPrice) {
        const isPriceWithoutDiscountSelected = hasPriceConfiguration && accommodationShoppingItem.priceConfiguration.priceWithoutDiscountSelected;
        const dpMetasMembershipPerksFee = isPriceWithoutDiscountSelected ? 0 : shoppingCart.membershipPerksFee;
        const membershipPerksFee = isMetasPricePresentation
            ? dpMetasMembershipPerksFee
            : shoppingCart.membershipPerksFee;
        return shoppingCart.totalPrice + (membershipPerksFee ? membershipPerksFee : 0) + (cheapestFee || 0);
    } else {
        return itineraryShoppingItem.price + shoppingCartPriceCalculatorDTO.markup +
            (accommodationShoppingItem ? accommodationShoppingItem.price : 0) - priceCalculator.discountSum;
    }
};

const pricingSelector = createSelector(
    productsSelector,
    itineraryPriceSelector,
    membershipFeeSelector,
    accommodationShoppingItemSelector,
    numTravellersSelector,
    getPrimeSelector,
    (products, {servicesTotalPrice, itineraryPricePaxDPNoFees, nonSelectableInsurancesTotalPrice, itineraryPrice, totalPrice, cheapestFee},
        membershipPerksFee,
        accommodationShoppingItem, numTravellers, isSubscriptionAdded) => {
        const baggagesSum = baggagesPrice(products.filter((prod) => prod.category === 'BAGGAGE'));
        const baggagesPerksFee = baggagesPrimePerks(products.filter((prod) => prod.category === 'BAGGAGE'), 'fromClient');
        const baggagesPerksFeeFromDapi = baggagesPrimePerks(products.filter((prod) => prod.category === 'BAGGAGE'), 'fromDapi');
        const cabinBagsSum = genericProductSum(products.filter((prod) => prod.category === 'BOARDING_OPTIONS'));
        const cabinBagsPerksFee = baggagesPrimePerks(products.filter((prod) => prod.category === 'BOARDING_OPTIONS'), 'fromClient');
        const cabinBagsPerksFeeFromDapi = baggagesPrimePerks(products.filter((prod) => prod.category === 'BOARDING_OPTIONS'), 'fromDapi');
        const seatsSum = seatsPrice(products.filter((prod) => prod.category === 'SEATS'));
        const seatsPerksFee = seatsPrimePerks(products.filter((prod) => prod.category === 'SEATS'), 'fromClient');
        const seatsPerksFeeFromDapi = seatsPrimePerks(products.filter((prod) => prod.category === 'SEATS'), 'fromDapi');
        const insuranceAndServicesSum =
            insuranceAndServicesPrice(products.filter((prod) => INSURANCES_TO_ADD.includes(prod.category)));
        const perksToAdd = isSubscriptionAdded ? membershipPerksFee : 0;
        const itineraryPricePerPaxWithPerks = (itineraryPrice + perksToAdd) / numTravellers;
        const itineraryMembershipPerksFee = membershipPerksFee - baggagesPerksFeeFromDapi - cabinBagsPerksFeeFromDapi - (seatsPerksFeeFromDapi || 0);
        const itineraryPricePerPaxPrimeFareSelected = (itineraryPrice + itineraryMembershipPerksFee) / numTravellers;
        const itineraryPricePerPaxFullFareSelected = itineraryPrice / numTravellers;
        const fastTrackSum = genericProductSum(products.filter((prod) => prod.category === 'FAST_TRACK'));

        return {
            membershipPerksFee: itineraryMembershipPerksFee,
            baggagesSum,
            baggagesPerksFee,
            cabinBagsSum,
            cabinBagsPerksFee,
            seatsSum,
            seatsPerksFee,
            itineraryPrice,
            nonSelectableInsurancesTotalPrice,
            itineraryPricePaxDPNoFees,
            accommodationShoppingItem,
            servicesTotalPrice,
            insuranceAndServicesSum,
            totalPrice,
            cheapestFee,
            itineraryPricePerPaxWithPerks,
            itineraryPricePerPaxPrimeFareSelected,
            itineraryPricePerPaxFullFareSelected,
            fastTrackSum,
        };
    }
);

export default {
    expectedPrice: (store) => expectedPriceSelector(store),
    repricingLog: (store) => repricingLogSelector(store),
    pricing: (store) => pricingSelector(store),
    selectedPaymentMethod: (store) => selectedPaymentMethodSelector(store),
    residentDiscountApplied: (store) => residentDiscountAppliedSelector(store),
};
